import React from 'react';
import {
  Mloc,
  PMCConfig,
  ProviderMatchProviderLocationV9
} from '@kyruus/types';
import KyruusTheme from '@kyruus/ui-theme';
import { LocationLink, ProviderBadge } from '@kyruus/provider-components';

import {
  LocationWrapper,
  LocationRow,
  LocationHeaderContainer
} from './styles';

import LocationAddress from './location-address';
import ProviderClinicHours from './provider-hours';
import LocationAccessibility from './location-accessibility';
import ProviderContact from './provider-contact';
import { withErrorBoundary } from '../ErrorFallback/with-error-boundary';
import { useMediaQuery } from '../../lib/hooks';
import messages from '../../lib/messages';
import { IntlShape, injectIntl } from '@kyruus/intl';

interface LocationsProps {
  location: ProviderMatchProviderLocationV9;
  googleMapsUrl?: string;
  getLocationUrl: (location: ProviderMatchProviderLocationV9) => string | null;
  locationCallback?: (locationUrl: string, isExternal: boolean) => void;
  mloc: Mloc;
  config: PMCConfig;
  intl: IntlShape;
}

const Location = ({
  location,
  mloc,
  config,
  getLocationUrl,
  locationCallback,
  intl
}: LocationsProps): React.ReactElement => {
  const isScreenMediumOrBelow = useMediaQuery(
    `(max-width:${KyruusTheme.screen_medium})`
  );
  const locationMicrodata = (() => {
    let isHospital = false;
    let isClinic = false;

    if (location.type) {
      isHospital = location.type === 'HOS';
      isClinic = location.type === 'CLI';
    } else if (location.name) {
      isHospital = location.name.toLowerCase().indexOf('hospital') !== -1;
      isClinic = location.name.toLowerCase().indexOf('clinic') !== -1;
    }

    if (isHospital) {
      return {
        itemProp: 'practicesAt',
        itemScope: true,
        itemType: 'https://schema.org/Hospital'
      };
    }

    if (isClinic) {
      return {
        itemProp: 'practicesAt',
        itemScope: true,
        itemType: 'https://schema.org/MedicalClinic'
      };
    }

    return {
      itemProp: 'practicesAt',
      itemScope: true,
      itemType: 'https://schema.org/MedicalOrganization'
    };
  })();

  const locationUrl = getLocationUrl(location);

  return (
    <LocationWrapper
      {...locationMicrodata}
      className="provider-profile-provider-location"
    >
      <LocationHeaderContainer>
        <LocationLink
          itemProp="name"
          location={location}
          getLocationUrl={getLocationUrl}
          locationCallback={locationCallback}
        >
          {location.name}
        </LocationLink>
        {locationUrl && <meta itemProp="url" content={locationUrl} />}
        {!isScreenMediumOrBelow &&
          location.provider_accepting_new_patients_at_location && (
            <ProviderBadge
              config={config}
              badge={{
                field: 'accepting_new_patients_at_location',
                value: true,
                label: intl.formatMessage(messages.acceptingNewPatientMessage)
              }}
            />
          )}
      </LocationHeaderContainer>

      <LocationRow
        itemProp="address"
        itemScope={true}
        itemType="https://schema.org/PostalAddress"
      >
        <LocationAddress location={location} config={config} />
      </LocationRow>

      <LocationRow>
        <ProviderContact location={location} contactType={'phone'} />
        <ProviderContact location={location} contactType={'fax'} />
        {mloc && mloc.hours_of_operation ? (
          <ProviderClinicHours
            periods={mloc.hours_of_operation.periods}
            timezone={location.timezone ?? undefined}
          />
        ) : null}
      </LocationRow>
      {location?.coordinates && (
        <div
          itemProp="geo"
          itemScope={true}
          itemType="https://schema.org/GeoCoordinates"
          style={{ display: 'none' }}
        >
          <meta
            itemProp="latitude"
            content={String(location.coordinates.lat)}
          />
          <meta
            itemProp="longitude"
            content={String(location.coordinates.lon)}
          />
        </div>
      )}
      {isScreenMediumOrBelow &&
        location.provider_accepting_new_patients_at_location && (
          <LocationRow id="accepting-new-patients-chip">
            <ProviderBadge
              config={config}
              badge={{
                field: 'accepting_new_patients_at_location',
                value: true,
                label: intl.formatMessage(messages.acceptingNewPatientMessage)
              }}
            />
          </LocationRow>
        )}
      {location?.accessibility && (
        <LocationRow>
          <LocationAccessibility location={location} />
        </LocationRow>
      )}
    </LocationWrapper>
  );
};

export default injectIntl(withErrorBoundary(Location));
